/**
 * Pure helper for building the Firestore document body of a new
 * `Responsibility`. Mirrors the iOS save path in
 * `AddResponsibilityView.swift:278-326` and the Android equivalent in
 * `AddResponsibilitySheet.kt:432-442`.
 *
 * The function is DOM-free and Firestore-free — it returns only the field
 * deltas that the caller passes to `addDoc(...)`. This keeps it unit-testable
 * without mocking either side, matching the pattern in
 * `responsibilities.ts` (`acceptReschedule` / `declineReschedule`).
 *
 * Validation note: required fields (title, householdId, createdBy) are
 * enforced via the return type — `null` means "form is invalid, do not
 * write". Callers should disable the submit button when this returns null.
 */

import { Timestamp } from "firebase/firestore";
import type { MemberProfile } from "./models";

export type RecurrenceValue = "none" | "daily" | "weekdays" | "weekly" | "monthly";

export interface CreateResponsibilityInput {
  title: string;
  description?: string;
  recurrence: RecurrenceValue;
  /** Required when `recurrence === "weekly"`, ignored otherwise. 0=Sun..6=Sat. */
  dayOfWeek?: number;
  /** Free-form "HH:mm" — emitted only when non-empty. Mirrors iOS `scheduledTime`. */
  effectiveTime?: string;
  /**
   * Custom verb shown on the action button (iOS: "Pick up", "Feed",
   * etc.). Mirrors `Responsibility.actionLabel`. Trimmed; empty
   * strings are dropped.
   */
  actionLabel?: string;
  /** Multi-link subjects — empty array is treated as "no link". */
  linkedEntityIds?: string[];
  householdId: string;
  createdBy: string;
  /**
   * Optional lookup so we can populate `linkedEntityType` ("member" | "pet")
   * from the first selected linked entity, matching iOS `linkedEntityType(for:)`
   * and Android `repository.householdMemberMap[firstId]?.isPet`.
   */
  memberLookup?: Record<string, MemberProfile>;
}

/**
 * Build the Firestore document body for a new Responsibility, or return
 * `null` if required fields are missing / invalid.
 *
 * Title is trimmed; an empty/whitespace-only title is rejected. `dayOfWeek`
 * is dropped unless `recurrence === "weekly"`. `effectiveTime` is dropped
 * when blank (after trim).
 *
 * The returned object intentionally does NOT include `uid` — Firestore
 * generates the document ID via `addDoc`.
 */
export function buildResponsibilityDoc(
  input: CreateResponsibilityInput,
): Record<string, unknown> | null {
  const title = input.title.trim();
  if (!title) return null;
  if (!input.householdId) return null;
  if (!input.createdBy) return null;

  const doc: Record<string, unknown> = {
    title,
    recurrence: input.recurrence,
    household_id: input.householdId,
    created_by: input.createdBy,
    created_at: Timestamp.now(),
  };

  const description = input.description?.trim();
  if (description) doc.description = description;

  // Day-of-week is only meaningful for weekly recurrence; the iOS form
  // discards it for daily/weekdays/monthly/none. Mirror that here so we
  // never persist a stale Sunday default for, say, a daily task.
  if (input.recurrence === "weekly" && typeof input.dayOfWeek === "number") {
    doc.dayOfWeek = input.dayOfWeek;
  }

  const time = input.effectiveTime?.trim();
  if (time) {
    // iOS calls this `scheduledTime` on the Responsibility model (see
    // models.ts) — match that field name so the iOS reader doesn't have
    // to learn an alias.
    doc.scheduledTime = time;
  }

  const actionLabel = input.actionLabel?.trim();
  if (actionLabel) {
    doc.actionLabel = actionLabel;
  }

  const ids = (input.linkedEntityIds ?? []).filter((id) => id && id.trim());
  if (ids.length > 0) {
    doc.linkedEntityIds = ids;
    // Backward-compat single-link field — iOS still reads this for older
    // responsibilities. Android's create path does the same.
    doc.linkedEntityId = ids[0];
    if (input.memberLookup) {
      const first = input.memberLookup[ids[0]];
      doc.linkedEntityType = first?.memberType === "pet" ? "pet" : "member";
    } else {
      doc.linkedEntityType = "member";
    }
  }

  return doc;
}

/** Re-export so consumers/tests don't need a separate import. */
export { Timestamp };
